import {
  createContext,
  useContext,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  CompassConfig,
  CompassParams,
  CompassZoomDomain,
} from '../../../types/compass';
import { CompassData } from '../../../hooks/scanners/useCompassData';
import { LegacyEquity, SynthOIEquity } from '../../../types/equityhub';

export interface CompassContextValue {
  // Persistable config
  config: CompassConfig;
  updateConfig: (patch: Partial<CompassConfig>) => void;

  // Derived compass params (computed from config with axis validation)
  compassParams: CompassParams;
  compassData: CompassData;

  // Transient UI state
  hoveredSymbol: string | undefined;
  setHoveredSymbol: Dispatch<SetStateAction<string | undefined>>;
  hoveredSymData: string | undefined;
  setHoveredSymData: Dispatch<SetStateAction<string | undefined>>;
  hiddenSymbols: Set<string>;
  setHiddenSymbols: Dispatch<SetStateAction<Set<string>>>;
  zoomDomain: CompassZoomDomain;
  setZoomDomain: Dispatch<SetStateAction<CompassZoomDomain>>;
  isZoomed: boolean;
  setIsZoomed: Dispatch<SetStateAction<boolean>>;
  zoomStartPoint: { x: number; y: number } | null;
  setZoomStartPoint: Dispatch<SetStateAction<{ x: number; y: number } | null>>;
  zoomEndPoint: { x: number; y: number } | null;
  setZoomEndPoint: Dispatch<SetStateAction<{ x: number; y: number } | null>>;
  isSelecting: boolean;
  setIsSelecting: Dispatch<SetStateAction<boolean>>;

  // User-level colors (from userSettings)
  posColor: string;
  negColor: string;

  // Whether the equity data backing this compass has call/put wall fields,
  // i.e. whether Proximity to Call/Put Wall axes may be offered.
  supportsWallAxes: boolean;

  // Explicit equity map override (e.g. the always-Total-OI map on /scanners).
  // Undefined means consumers should fall back to the global model-dependent map.
  equityMap?: Map<string, LegacyEquity | SynthOIEquity>;
}

export const CompassContext = createContext<CompassContextValue | null>(null);

export const useCompassCtx = (): CompassContextValue => {
  const ctx = useContext(CompassContext);
  if (ctx == null) {
    throw new Error('useCompassCtx must be used within a CompassProvider');
  }
  return ctx;
};
